import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  AutoStoriesRounded,
  SchoolRounded,
  WorkHistoryRounded,
  WorkspacePremiumRounded,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import api from "lib/api/api";
import Meta from "../Meta/Meta";
import TeacherFeedbackSection from "../TeacherFeedback/TeacherFeedbackSection";

const staticTeamImages = [
  { name: "Mafdy Farouk", image: "/team-mafdy-farouk.jpg" },
  { name: "Mohamed Abdulsattar", image: "/team-mohamed-abdulsattar.jpg" },
  { name: "Miriam Dirnberger", image: "/team-miriam-dirnberger.jpg" },
  { name: "Sara Elrefaey", image: "/team-sara-elrefaey.jpg" },
  { name: "Erik Lutz", image: "/team-erik-lutz.jpeg" },
  { name: "Hoda Mostafa", image: "/team-hoda-mostafa.jpg" },
  { name: "Fatema Alzahraa", image: "/team-fatema-alzahraa.JPG" },
  { name: "Yara Ahmed", image: "/team-yara-ahmed.JPG" },
];

const getTeacherImageSrc = (value) => {
  if (!value) return "";
  const src = String(value).trim();
  if (src.startsWith("/api/")) return `${process.env.REACT_APP_API_URL}${src}`;
  if (/^(https?:)?\/\//i.test(src) || src.startsWith("/")) return src;
  return `/${src}`;
};

const TeacherDetailsPage = () => {
  const { businessKey } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["ui"]);
  const language = i18n.language?.split("-")[0];
  const isArabic = language === "ar";
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    let ignore = false;

    const loadTeacher = async () => {
      setLoading(true);
      setError(null);
      try {
        const isLegacyId = /^\d+$/.test(businessKey || "");
        const endpoint = isLegacyId
          ? `/courses/teachers/${businessKey}`
          : `/courses/teachers/key/${encodeURIComponent(businessKey)}`;
        const response = await api.get(endpoint);
        if (!ignore) {
          setTeacher(response.data);
          if (response.data?.businessKey && response.data.businessKey !== businessKey) {
            navigate(`/teachers/${response.data.businessKey}`, { replace: true });
          }
        }
      } catch (err) {
        if (!ignore) setError(err);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    loadTeacher();
    return () => {
      ignore = true;
    };
  }, [businessKey, navigate]);

  if (loading) {
    return (
      <Box sx={{ py: 10, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !teacher) {
    return (
      <Box sx={{ py: 10, textAlign: "center" }}>
        <Typography color="error">
          {t("teacherDetails.notFound", { defaultValue: "Teacher not found." })}
        </Typography>
      </Box>
    );
  }

  const teacherName = `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim();
  const fallbackImage = staticTeamImages.find(
    (member) => member.name.toLowerCase() === teacherName.toLowerCase()
  )?.image;
  const imageSrc = getTeacherImageSrc(teacher.profilePicture) || fallbackImage;
  const showImage = imageSrc && !imageFailed;
  const localized = (defaultValue, arValue, deValue) => {
    if (language === "ar" && arValue) return arValue;
    if (language === "de" && deValue) return deValue;
    return defaultValue;
  };
  const teacherBio = localized(teacher.bio, teacher.bioAr, teacher.bioDe);
  const teacherSpecialization = localized(
    teacher.specialization,
    teacher.specializationAr,
    teacher.specializationDe
  );
  const teacherStudy = localized(teacher.study, teacher.studyAr, teacher.studyDe);
  const teacherExperience = localized(
    teacher.experience,
    teacher.experienceAr,
    teacher.experienceDe
  );
  const teacherAcademicQualifications = localized(
    teacher.academicQualifications,
    teacher.academicQualificationsAr,
    teacher.academicQualificationsDe
  );
  const teacherCourseDescription = localized(
    teacher.courseDescription,
    teacher.courseDescriptionAr,
    teacher.courseDescriptionDe
  );
  const profileItems = [
    {
      label: t("courseDetails.teacherProfile.study", { defaultValue: "Study" }),
      value: teacherStudy,
      icon: <SchoolRounded />,
    },
    {
      label: t("courseDetails.teacherProfile.experience", { defaultValue: "Experience" }),
      value: teacherExperience,
      icon: <WorkHistoryRounded />,
    },
    {
      label: t("courseDetails.teacherProfile.specialization", { defaultValue: "Specialization" }),
      value: teacherSpecialization,
      icon: <AutoStoriesRounded />,
    },
  ];

  return (
    <>
      <Meta
        title={`${teacherName} | ALGermanist`}
        description={`Teacher profile for ${teacherName}.`}
        keywords={`ALGermanist teacher, ${teacherName}`}
      />
      <Box sx={{ bgcolor: "#F3F5F5", py: { xs: 3, md: 7 }, minHeight: "70vh" }} dir={isArabic ? "rtl" : "ltr"}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 5 } }}>
          <Button
            startIcon={!isArabic ? <ArrowBack /> : null}
            endIcon={isArabic ? <ArrowBack /> : null}
            onClick={() => navigate(-1)}
            sx={{ mb: 3, textTransform: "none", fontWeight: 700 }}
          >
            {t("teacherDetails.back", { defaultValue: "Back" })}
          </Button>

          <Box sx={{ bgcolor: "#fff", borderRadius: { xs: 3, md: 5 }, overflow: "hidden", boxShadow: "0 18px 55px rgba(24,39,39,.09)", mb: 3 }}>
            <Grid container sx={{ direction: "ltr" }}>
              <Grid item xs={12} md={4.25} sx={{ order: { xs: 1, md: isArabic ? 2 : 1 }, bgcolor: "#E9EEEE", minHeight: { xs: 360, md: 560 } }}>
                {showImage ? (
                  <Box
                    component="img"
                    src={imageSrc}
                    alt={teacherName}
                    onError={(event) => {
                      if (fallbackImage && !event.currentTarget.src.endsWith(fallbackImage)) {
                        event.currentTarget.src = fallbackImage;
                        return;
                      }
                      setImageFailed(true);
                    }}
                    sx={{
                      width: "100%",
                      height: "100%",
                      minHeight: { xs: 360, md: 560 },
                      objectFit: "cover",
                      objectPosition: "center top",
                      bgcolor: "#F6F8F8",
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: "100%",
                      height: "100%",
                      minHeight: { xs: 360, md: 560 },
                      borderRadius: 0,
                      bgcolor: "#FFD600",
                      color: "#111",
                      fontSize: "5rem",
                      fontWeight: 800,
                    }}
                  >
                    {teacher.firstName?.[0]}
                    {teacher.lastName?.[0]}
                  </Avatar>
                )}
              </Grid>

              <Grid item xs={12} md={7.75} sx={{ order: { xs: 2, md: isArabic ? 1 : 2 }, p: { xs: 3, sm: 4, md: 6 }, direction: isArabic ? "rtl" : "ltr", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Typography sx={{ color: "#987600", fontWeight: 900, letterSpacing: ".08em", textTransform: "uppercase", mb: 1.5 }}>
                  {t("teacherDetails.profile", { defaultValue: "Meet your teacher" })}
                </Typography>
                <Typography variant="h2" fontWeight={900} sx={{ mb: 1.5, lineHeight: 1.05, fontSize: { xs: "2.5rem", md: "4.25rem" }, letterSpacing: "-.035em" }}>
                  {teacherName}
                </Typography>
                {(teacherSpecialization || teacher.job) && (
                  <Chip label={teacherSpecialization || teacher.job} sx={{ alignSelf: isArabic ? "flex-end" : "flex-start", bgcolor: "#FFD600", color: "#111", fontWeight: 800, mb: 3, height: "auto", py: .5, "& .MuiChip-label": { whiteSpace: "normal" } }} />
                )}

                {teacherBio && (
                  <Typography sx={{ mb: 4, lineHeight: 1.9, whiteSpace: "pre-line", color: "#465151", fontSize: { xs: "1rem", md: "1.12rem" }, maxWidth: 760 }}>
                    {teacherBio}
                  </Typography>
                )}

                <Grid container spacing={2}>
                  {profileItems.map((item) => (
                    <Grid item xs={12} sm={4} key={item.label}>
                      <Box
                        sx={{
                          border: "1px solid #DFE5E5",
                          borderRadius: 3,
                          p: 2.25,
                          height: "100%",
                          bgcolor: "#FAFBFB",
                        }}
                      >
                        <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: "#FFF1A8", color: "#6D5800", display: "grid", placeItems: "center", mb: 1.5 }}>{item.icon}</Box>
                        <Typography fontWeight={900} sx={{ mb: 0.75 }}>{item.label}</Typography>
                        <Typography sx={{ color: "#303A3A", lineHeight: 1.55 }}>
                          {item.value || "-"}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={3} sx={{ direction: isArabic ? "rtl" : "ltr" }}>
            {[
              { icon: <WorkspacePremiumRounded />, title: t("courseDetails.teacherProfile.academicQualifications", { defaultValue: "Academic Qualifications" }), value: teacherAcademicQualifications, tone: "#FFF8D5" },
              { icon: <AutoStoriesRounded />, title: t("courseDetails.teacherProfile.myCourses", { defaultValue: "Teaching approach" }), value: teacherCourseDescription, tone: "#EAF1F1" },
            ].map((section) => (
              <Grid item xs={12} md={6} key={section.title}>
                <Box sx={{ bgcolor: "#fff", borderRadius: 4, p: { xs: 3, md: 4 }, height: "100%", boxShadow: "0 8px 28px rgba(24,39,39,.06)", border: "1px solid #E7EBEB" }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5 }}>
                    <Box sx={{ width: 50, height: 50, borderRadius: 3, bgcolor: section.tone, color: "#282400", display: "grid", placeItems: "center", flexShrink: 0 }}>{section.icon}</Box>
                    <Typography fontWeight={900} sx={{ fontSize: { xs: "1.35rem", md: "1.55rem" } }}>{section.title}</Typography>
                  </Stack>
                  <Typography sx={{ color: "#465151", lineHeight: 1.9, whiteSpace: "pre-line", fontSize: "1.04rem" }}>{section.value || "-"}</Typography>
                  {section.title === t("courseDetails.teacherProfile.academicQualifications", { defaultValue: "Academic Qualifications" }) && teacher.qualifications?.length > 0 && (
                    <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} sx={{ mt: 3 }}>
                      {teacher.qualifications.map((qualification) => <Chip key={qualification} label={qualification} variant="outlined" />)}
                    </Stack>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
          <TeacherFeedbackSection teacherId={teacher.teacherId} teacherName={teacherName} placement="profile" />
        </Container>
      </Box>
    </>
  );
};

export default TeacherDetailsPage;
